// Ponte con l'app nativa (SSO app -> volantino).
//
// L'app, prima del caricamento della pagina, inietta:
//   window.__INPUBLISH_APP__ = { v: 1, session: { jwtToken, expiresAt, user } | null }
// Da quel momento l'app e' l'unica a gestire login, logout e rinnovo del
// token: il volantino non conosce il refresh token e, quando il token non
// basta piu', lo chiede all'app.
//
// App -> volantino: window.dispatchEvent(new CustomEvent("inpublish:session", { detail: session | null }))
// Volantino -> app: ReactNativeWebView.postMessage({ action: "auth_required" | "login_required" })
//
// Senza __INPUBLISH_APP__ (browser, app vecchie) o per i tenant legacy senza
// x_client_token nulla di questo si attiva.
// Dipende solo da fakeServer: questo file lo importa api.services, e passare
// da helper/utility creerebbe un ciclo di import (utility -> manager ->
// Form.component -> api.services).
import { serverResponse } from "../util/fakeServer";

// Stesso discriminatore di hasNewCheckout(): stack v2 solo con x_client_token.
const hasClientToken = () => {
  const token = serverResponse.config.x_client_token;
  return typeof token === "string" && token.trim() !== "";
};

const AUTH_TOKENS_KEY = "inpublish_auth_tokens";
const AUTH_USER_KEY = "inpublish_auth_user";
const SESSION_EVENT = "inpublish:session";
const REQUEST_TIMEOUT_MS = 8000;

export const isAppSession = () =>
  typeof window !== "undefined" &&
  !!window.__INPUBLISH_APP__ &&
  hasClientToken();

export const isSessionValid = (session, marginMs = 0) =>
  !!(session && session.jwtToken && session.expiresAt > Date.now() + marginMs && session.user);

export const postToApp = (message) => {
  try {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  } catch { /* ignore */ }
};

// Utente nel formato usato dal volantino (vedi loginJWT).
export const normalizeAppUser = (user = {}) => ({
  ...user,
  apiUserId: user.apiUserId || user.id,
});

// Scrive la sessione dell'app dove la legge il resto del volantino
// (reducer, fetchWithAuth). Nessun refresh token: resta nell'app.
export const writeSessionToStorage = (session) => {
  try {
    if (session && session.jwtToken && session.user) {
      localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify({
        jwtToken: session.jwtToken,
        refreshToken: null,
        expiresAt: session.expiresAt,
      }));
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(normalizeAppUser(session.user)));
    } else {
      localStorage.removeItem(AUTH_TOKENS_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
    }
  } catch { /* ignore */ }
};

// Da chiamare prima che il reducer legga il localStorage: in modalita' app
// conta solo la sessione passata dall'app, mai una rimasta da prima. Una
// sessione scaduta viene scritta lo stesso: getProfileJWT la rinnova.
export const syncAppSessionToStorage = () => {
  if (isAppSession()) writeSessionToStorage(window.__INPUBLISH_APP__.session || null);
};

// Ogni sessione inviata dall'app (risposta a auth_required, login o logout
// fatti nell'app). Restituisce la funzione per smettere di ascoltare.
export const onAppSession = (callback) => {
  const handler = (e) => callback(e.detail || null);
  window.addEventListener(SESSION_EVENT, handler);
  return () => window.removeEventListener(SESSION_EVENT, handler);
};

let pendingRequest = null;

// Chiede all'app una sessione aggiornata. Richieste contemporanee
// condividono la stessa attesa; senza risposta entro il timeout -> null.
export const requestSession = () => {
  if (!isAppSession()) return Promise.resolve(null);
  if (pendingRequest) return pendingRequest;

  pendingRequest = new Promise((resolve) => {
    let timer = null;
    const stop = onAppSession((session) => {
      clearTimeout(timer);
      stop();
      resolve(isSessionValid(session) ? session : null);
    });
    timer = setTimeout(() => {
      stop();
      resolve(null);
    }, REQUEST_TIMEOUT_MS);
    postToApp({ action: "auth_required" });
  }).finally(() => {
    pendingRequest = null;
  });

  return pendingRequest;
};

export const requestLogin = () => postToApp({ action: "login_required" });
